<script setup lang="ts">
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";
import WrDesignShineText from "~/components/molecules/WrDesignShineText.vue";
import WrDesignStaggeredProcessCards from "~/components/molecules/WrDesignStaggeredProcessCards.vue";
import WrDesignTailoredSection from "~/components/molecules/WrDesignTailoredSection.vue";
import WrProblemSolutionSection from "~/components/molecules/WrProblemSolutionSection.vue";
import WrSelectedProject from "~/components/molecules/WrSelectedProject.vue";
import WrOurServiceSection from "~/components/molecules/WrOurServiceSection.vue";

// Lazy-loaded components (below the fold)
const LazyWrDesignScenarioCards = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignScenarioCards.vue")
);
const LazyWrDesignLifecycleFlowDiagram = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignLifecycleFlowDiagram.vue")
);
const LazyWrDesignServiceCardsSection = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignServiceCardsSection.vue")
);
const LazyWrDesignFourCardTransformGridSection = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignFourCardTransformGridSection.vue")
);
const LazyWrDesignReasonsSection = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignReasonsSection.vue")
);
const LazyWrDesignReadyToElevateYourBrand = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignReadyToElevateYourBrand.vue")
);
const LazyWrDesignBenefitCards = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignBenefitCards.vue")
);
const LazyWrDesignFaqsSection = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignFaqsSection.vue")
);
const LazyWrDesignStatsCard = defineLazyHydrationComponent("visible",
  () => import("~/components/molecules/WrDesignStatsCard.vue")
);
const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue")
);

/* ─── State & Composables ─── */
const visible = ref<boolean>(false);
const openModal = (): void => {
  visible.value = true;
};

/* ─── Fetch Content ─── */
const { data } = await useFetch(
  `${useRequestURL().origin}/content/services/development-services/product-engineering-content-modern.json`,
);
if (!data.value) throw createError({ statusCode: 404, statusMessage: "Page content not found" });
const content = data.value as any;

const {
  meta_tags,
  hero,
  shineText,
  quickHits,
  businessOutcomes,
  whyItMatters,
  capabilities,
  processMotion,
  techStack,
  integrationCards: integrationCardsData,
  useCases,
  whyChooseUs,
  cta,
  faq,
} = content;

/* ─── SEO Metadata & Schema ─── */
useHead({
  title: meta_tags.title,
  meta: [...meta_tags.meta, { name: "robots", content: "index, follow" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta_tags.serviceSchema.name,
        serviceType: meta_tags.serviceSchema.serviceType,
        url: meta_tags.canonical,
        provider: {
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
        },
        description: meta_tags.serviceSchema.description,
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://webreinvent.com" },
          { "@type": "ListItem", position: 2, name: "Services", item: "https://webreinvent.com/services" },
          { "@type": "ListItem", position: 3, name: meta_tags.serviceSchema.breadcrumbName, item: meta_tags.canonical },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.faqItems.map((item: { title: string; description: string }) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: { "@type": "Answer", text: item.description },
        })),
      }),
    },
  ],
});

useSeoMeta({
  ogTitle: meta_tags.meta.find((m: any) => m.property === 'og:title')?.content || meta_tags.title,
  ogDescription: meta_tags.meta.find((m: any) => m.property === 'og:description')?.content,
  ogUrl: meta_tags.canonical,
  ogType: 'website',
  twitterTitle: meta_tags.meta.find((m: any) => m.name === 'twitter:title')?.content,
  twitterDescription: meta_tags.meta.find((m: any) => m.name === 'twitter:description')?.content,
  twitterImage: meta_tags.meta.find((m: any) => m.name === 'twitter:image')?.content,
  twitterCard: 'summary_large_image',
});

definePageMeta({
  layout: "services-layout",
});

/* ─── Mapped Data ─── */

// StaggeredProcessCards: { number, title, desc }
const staggeredSteps = computed(() =>
  quickHits.items.map((item: any) => ({
    number: item.id,
    title: item.title,
    desc: item.description,
  }))
);

const businessOutcomeCards = computed(() =>
  businessOutcomes.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

const capabilityCards = computed(() =>
  capabilities.items.map((card: any) => ({
    title: card.title,
    problem: card.problem,
    solution: card.solution,
    result: card.result,
  }))
);

// LifecycleFlowDiagram: { id, icon, title, subtitle, items: { label }[] }
const lifecycleStages = computed(() =>
  processMotion.items.map((card: any, index: number) => ({
    id: String(index + 1).padStart(2, '0'),
    icon: card.iconName,
    title: card.title,
    subtitle: card.description,
    items: card.items || [],
  }))
);

const techStackCards = computed(() =>
  techStack.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    desc: card.description,
  }))
);

// FourCardTransformGrid: { title, description, svgIcon }
const transformCards = computed(() =>
  integrationCardsData.items.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

const useCaseCards = computed(() =>
  useCases.items.map((card: any) => ({
    title: card.title,
    desc: card.desc,
  }))
);
const useCaseIcons = computed(() => useCases.svgIcons);

const whyChooseUsCards = computed(() =>
  whyChooseUs.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

const ctaCards = computed(() =>
  cta.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

const faqItems = computed(() => faq.faqItems);

const heroAlt = computed(() => String(hero.heading).replace(/<[^>]*>/g, '').trim());
</script>

<template>
  <main class="min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

      <!-- Section 1: Hero Card -->
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="hero.heading"
          :subcopy="hero.subcopy"
          :subcopy-class="hero.subcopyClass"
          :cta-text="hero.ctaText"
          :image-src="hero.imageSrc"
          :light-image-src="hero.lightImageSrc"
          :alt="heroAlt"
          @cta-clicked="openModal"
        />
      </header>

      <!-- Section 1.5: Shine Text -->
      <section class="my-8" aria-label="Core Value Statement">
        <WrDesignShineText :text="shineText" />
      </section>

      <!-- Section 2: Quick Hits (Staggered Process Cards) -->
      <section aria-label="Product Engineering Capabilities at a Glance">
        <WrDesignStaggeredProcessCards
          :heading="quickHits.heading"
          :subcopy="quickHits.subcopy"
          :steps="staggeredSteps"
          :cta-text="quickHits.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 3: Business Outcomes -->
      <section aria-label="Business Outcomes">
        <WrDesignTailoredSection
          :heading="businessOutcomes.heading"
          :subcopy="businessOutcomes.subcopy"
          :cards="businessOutcomeCards"
          :cta-text="businessOutcomes.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 4: Problem / Solution -->
      <section aria-labelledby="why-it-matters-heading">
        <WrProblemSolutionSection
          id="why-it-matters-heading"
          :heading="whyItMatters.heading"
          :intro="whyItMatters.subcopy"
          :problem-label="whyItMatters.lossLabel"
          :problems="whyItMatters.losses"
          :solution-label="whyItMatters.deliverLabel"
          :solutions="whyItMatters.delivers"
          :quote="whyItMatters.quote"
          :cta-text="whyItMatters.ctaText"
          :bg-image-light="whyItMatters.bgImageLight"
          :bg-image-dark="whyItMatters.bgImageDark"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 5: Capabilities -->
      <section aria-label="Engineering Capabilities">
        <LazyWrDesignScenarioCards
          :heading="capabilities.heading"
          :subcopy="capabilities.subcopy"
          :cards="capabilityCards"
        />
      </section>

      <!-- Section 6: How Product Engineering Works (Lifecycle Flow Diagram) -->
      <section aria-label="Product Engineering Process">
        <LazyWrDesignLifecycleFlowDiagram
          :title="processMotion.heading"
          :stages="lifecycleStages"
        />
      </section>


      <!-- Section 8: Integration Cards (Four Card Transform Grid) -->
      <section aria-label="Technology Integration">
        <LazyWrDesignFourCardTransformGridSection
          :heading="integrationCardsData.singleLineHeading"
          :subtitle="integrationCardsData.subcopy"
          :cards="transformCards"
          :cta-text="integrationCardsData.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 9: Real-World Use Cases -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Real-World Use Cases">
        <LazyWrDesignReasonsSection
          :heading="useCases.heading"
          :subcopy="useCases.subcopy"
          :cards="useCaseCards"
          :svg-icons="useCaseIcons"
          :cta-text="useCases.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 10: Why Choose Us -->
      <section aria-label="Why Choose WebReinvent">
        <LazyWrDesignReadyToElevateYourBrand
          :heading="whyChooseUs.heading"
          :subcopy="whyChooseUs.subcopy"
          :cards="whyChooseUsCards"
          :cta-text="whyChooseUs.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 11: CTA Block -->
      <section aria-label="Call to Action">
        <LazyWrDesignBenefitCards
          :heading="cta.heading"
          :subcopy="cta.subcopy"
          :cards="ctaCards"
        />
        <div v-if="cta.ctaText" class="flex justify-center mt-8">
          <WrButton
            @click="openModal"
            container-class="h-[48px]"
            append-class="text-base font-normal px-6"
          >
            {{ cta.ctaText }}
          </WrButton>
        </div>
      </section>

      <!-- Section 11.5: Products We Built -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Products We Built">
        <WrSelectedProject />
      </section>

      <!-- Section 11.6: Our Services -->
      <section aria-label="Our Services">
        <WrOurServiceSection
          title="Explore Our Services"
          description="Discover our full range of development and integration capabilities."
        />
      </section>

      <!-- Section 12: FAQ -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Frequently asked questions">
        <LazyWrDesignFaqsSection
          :heading="faq.heading"
          :suffix="faq.suffix"
          :subtext="faq.subtext"
          :filter-tags="faq.filterTags"
          :faq-items="faqItems"
          :cta-text="faq.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 13: Testimonials -->
      <section class="relative overflow-hidden dark:bg-none mt-24 lg:mt-[100px] mb-16 lg:mb-[100px] z-20" aria-label="Client Testimonials">
        <div class="container-wrapper-xl">
          <h2 class="text-center mb-11">
            <WrGradientText
              text="Trusted for excellence and efficiency"
              append-class="text-3xl lg:text-[48px]"
            />
            <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
              WebReinvent is trusted by leading organisations to enhance their
              development and operational efficiency.
            </p>
          </h2>
          <WrTestimonialCardCarousel class="mb-12" />
          <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
            >View All</WrButton
          >
        </div>
      </section>

      <!-- Section 14: Stats Card -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Company Statistics">
        <LazyWrDesignStatsCard />
      </section>

    </div>
  </main>

  <LazyWrContactFormPopup :isOpen="visible" class="z-30" @close="visible = false" />
</template>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>